import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Button, Message, Loader, Popup, Icon, Menu, Pagination, Header } from 'semantic-ui-react';
import RejectionModal from './RejectionModal';
import CreationLogsModal from './CreationLogsModal';
import RequestDetailsModal from './RequestDetailsModal';
import RejectionSuccessModal from './RejectionSuccessModal';
import ApprovalSuccessModal from './ApprovalSuccessModal';
import VMCreationErrorModal from './VMCreationErrorModal';
import SubscriptionSelectionModal from './SubscriptionSelectionModal';
import axios from 'axios';
import { formatTimestamp } from '../utils/timezone.utils';
import { useTimezone } from '../hooks/useTimezone';
import { getBatchBadges } from '../utils/requestHelpers';
import apiClient from '../api/apiClient';
import useProvisioningLogs from '../hooks/useProvisioningLogs';

const ApprovalView = ({
    userEmail,
    userRoles,
    approverCategories,
    approverCategoriesSingleTenancy = [],
    approverCategoriesMultiTenancy = [],
    linodeEnabled = false
}) => {
    const userTimezone = useTimezone(); // Listen to timezone changes for re-renders
    const [rejectedRequest, setRejectedRequest] = useState(null);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [creationLogs, setCreationLogs] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showRejectionSuccessModal, setShowRejectionSuccessModal] = useState(false);
    const [showApprovalSuccessModal, setShowApprovalSuccessModal] = useState(false);
    const [processingApprovalId, setProcessingApprovalId] = useState(null);
    const [processingRejectionId, setProcessingRejectionId] = useState(null);
    const [vmCreationError, setVMCreationError] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [infoBanner, setInfoBanner] = useState('');
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [subscriptions, setSubscriptions] = useState([]);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
    const [isCancelled, setIsCancelled] = useState(false);
    const [abortController, setAbortController] = useState(null);
    const [logViewRequestId, setLogViewRequestId] = useState(null);

    const {
        logs: persistedLogs,
        startedAt: provisioningStartedAt,
        cancelProvisioning
    } = useProvisioningLogs(logViewRequestId, userEmail, {
        // Drive the live modal from the server store + socket/poll (not the fragile HTTP stream)
        enabled: showLogsModal && !!logViewRequestId
    });
    const [rowsPerPage, setRowsPerPage] = useState(() => parseInt(localStorage.getItem('tb_rowsPerPage') || '25', 10));
    const [tableDensity, setTableDensity] = useState(() => localStorage.getItem('tb_tableDensity') || 'comfortable');
    const [activePage, setActivePage] = useState(1);
    const [autoProvisionInfo, setAutoProvisionInfo] = useState({
        enabled: false,
        categories: []
    });


    const safeGet = (obj, path, defaultValue = 'N/A') => {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : defaultValue;
        }, obj);
    };

    const getCloudName = (request) => {
        const cloudName = safeGet(request, 'formData.cloudName', '');
        return cloudName ? cloudName.toLowerCase() : 'unknown';
    };

    const getVmSize = (request) => {
        return safeGet(request, 'formData.vmSize', 'Unknown');
    };

    const getSubmittedBy = (request) => {
        return safeGet(request, 'formData.submittedBy', 'Unknown');
    };

    const isAutoProvisionRequestForUser = (request) => {
        try {
            const submittedBy = getSubmittedBy(request);
            const isOwner = submittedBy && submittedBy.toLowerCase() === userEmail.toLowerCase();
            const isAutoProvision = !!(request.formData && request.formData.autoProvision);
            const category = request.category;
            const hasCategoryAccess =
                category &&
                autoProvisionInfo.enabled &&
                Array.isArray(autoProvisionInfo.categories) &&
                autoProvisionInfo.categories.some(
                    (c) =>
                        c &&
                        c.trim().toUpperCase() === category.trim().toUpperCase()
                );
            return isOwner && isAutoProvision && hasCategoryAccess;
        } catch {
            return false;
        }
    };

    const isApprover = userRoles.includes('APPROVER') || autoProvisionInfo.enabled;
    const isSuperAdmin = userRoles.includes('DEVOPS');

    // Sorting state for approver table
    const [sortColumn, setSortColumn] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('descending'); // 'ascending' | 'descending'

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortColumn(column);
            // Default to descending for timestamp, ascending otherwise
            setSortDirection(column === 'timestamp' ? 'descending' : 'ascending');
        }
    };

    const handleCancelOperation = async () => {
        const targetId = logViewRequestId || processingApprovalId;
        setIsCancelled(true);
        setCreationLogs(prev => [...prev, `Operation cancelled by ${userEmail}`]);
        try {
            if (targetId) {
                await apiClient.post(`/api/requests/${targetId}/cancel-provisioning`);
            } else if (typeof cancelProvisioning === 'function') {
                await cancelProvisioning();
            }
        } catch (err) {
            console.error('Cancel provisioning failed:', err);
        }
        if (abortController) {
            abortController.abort();
        }
    };

    const isRequestForm3 = (request) => {
        return request.formData && request.formData.requestForm3 === true;
    };

    const isMigrationRequest = (request) => {
        return !!(request.formData && request.formData.isMigrationSetup);
    };

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const response = await axios.get('https://qa-tenantbridge.parablu.com/api/requests', {
                withCredentials: true
            });

            const validRequests = response.data.requests.filter(request => {
                if (!request || !request.id || !request.formData) {
                    console.warn('Skipping invalid request:', request);
                    return false;
                }

                if (!request.formData.cloudName || !request.formData.submittedBy) {
                    console.warn('Skipping request with missing formData:', request.id);
                    return false;
                }

                return true;
            });

            const filteredRequests = validRequests.filter(request => {
                const hasUserApproved = request.approver?.split(',')
                    .map(email => email.trim().toLowerCase())
                    .includes(userEmail.toLowerCase());

                const autoProvisionForUser = isAutoProvisionRequestForUser(request);

                // Auto provisioning: user acts as approver for their own requests
                if (autoProvisionForUser && request.status === 'PENDING_APPROVER') {
                    return !hasUserApproved;
                }

                // Check if user is assigned as approver for this request
                const isUserAssignedApprover = request.currentApprover?.split(',')
                    .map(email => email.trim().toLowerCase())
                    .includes(userEmail.toLowerCase());

                // Show MIGRATION IS IN PROGRESS requests to approvers who approved them
                if (request.status === 'MIGRATION IS IN PROGRESS') {
                    if (isApprover || isSuperAdmin) {
                        // Show if user is in the approver list or is DevOps
                        const isInApproverList = request.approver?.split(',')
                            .map(email => email.trim().toLowerCase())
                            .includes(userEmail.toLowerCase());
                        return isInApproverList || isSuperAdmin;
                    }
                    return false;
                }

                // Migration requests: Show to DEVOPS users if they are assigned as approver
                if (isMigrationRequest(request) && request.status === 'PENDING_APPROVER') {
                    if (isSuperAdmin && isUserAssignedApprover) {
                        return !hasUserApproved;
                    }
                    // Migration requests should not be shown to regular approvers
                    return false;
                }

                if (isApprover && request.status === 'PENDING_APPROVER') {
                    // Only requestForm3 indicates a true Multi Tenancy Form request
                    // isMultiTenancySetup is just a hosting type within ST form, so it should use ST approver categories
                    const isMultiTenancy = !!(request.formData?.requestForm3);
                    
                    // Filter by approver's assigned categories (ST or MT based on request type)
                    const relevantApproverCategories = isMultiTenancy 
                        ? approverCategoriesMultiTenancy 
                        : approverCategoriesSingleTenancy;
                    
                    // Fallback to old approverCategories if new fields are empty (backward compatibility)
                    const categoriesToCheck = (relevantApproverCategories && relevantApproverCategories.length > 0) 
                        ? relevantApproverCategories 
                        : (approverCategories || []);
                    
                    if (categoriesToCheck.length > 0 && request.category) {
                        const requestCategory = request.category.trim().toUpperCase();
                        const hasMatchingCategory = categoriesToCheck.some(cat => 
                            cat && cat.trim().toUpperCase() === requestCategory
                        );
                        // Only show requests matching approver's categories
                        return !hasUserApproved && hasMatchingCategory;
                    }
                    // If no categories assigned, don't show any requests (safety measure)
                    return false;
                }

                return false;
            });

            setRequests(filteredRequests);
            setError(null);
        } catch (err) {
            console.error('Error fetching requests:', err);
            setError('Failed to load requests: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Load auto provisioning access for current user
    useEffect(() => {
        const fetchAutoProvisionInfo = async () => {
            try {
                const response = await axios.get('/api/user/roles', {
                    withCredentials: true,
                    timeout: 5000
                });
                setAutoProvisionInfo({
                    enabled: !!response.data.autoProvisionEnabled,
                    categories: response.data.autoProvisionCategories || []
                });
            } catch (err) {
                setAutoProvisionInfo({
                    enabled: false,
                    categories: []
                });
            }
        };

        if (userEmail) {
            fetchAutoProvisionInfo();
        }
    }, [userEmail]);

    // Listen for preference changes (from same tab - custom event)
    useEffect(() => {
        const handler = (e) => {
            if (e.detail?.rowsPerPage) setRowsPerPage(parseInt(e.detail.rowsPerPage, 10));
            if (e.detail?.tableDensity) setTableDensity(e.detail.tableDensity);
            setActivePage(1);
        };
        window.addEventListener('tb:prefsChanged', handler);
        return () => window.removeEventListener('tb:prefsChanged', handler);
    }, []);

    // Listen for localStorage changes from other tabs
    useEffect(() => {
        const storageHandler = (e) => {
            if (e.key === 'tb_rowsPerPage' && e.newValue) {
                setRowsPerPage(parseInt(e.newValue, 10));
                setActivePage(1);
            }
            if (e.key === 'tb_tableDensity' && e.newValue) {
                setTableDensity(e.newValue);
            }
        };
        window.addEventListener('storage', storageHandler);
        return () => window.removeEventListener('storage', storageHandler);
    }, []);

    const copyToClipboard = async (text, successMessage = 'Copied to clipboard') => {
        try {
            await navigator.clipboard.writeText(String(text));
            setInfoBanner(successMessage);
            setTimeout(() => setInfoBanner(''), 2500);
        } catch (e) {
            setError('Copy failed');
            setTimeout(() => setError(''), 2500);
        }
    };

    const EmptyState = ({ userRoles, approverCategories, approverCategoriesSingleTenancy, approverCategoriesMultiTenancy }) => {
        const isApprover = userRoles.includes('APPROVER');
        const hasSTCategories = approverCategoriesSingleTenancy && approverCategoriesSingleTenancy.length > 0;
        const hasMTCategories = approverCategoriesMultiTenancy && approverCategoriesMultiTenancy.length > 0;
        const hasAnyCategories = hasSTCategories || hasMTCategories || (approverCategories && approverCategories.length > 0);
        
        if (isApprover && hasAnyCategories) {
            return (
                <Message info>
                    <Message.Header>No Pending Requests</Message.Header>
                    <p>There are no requests pending your approval in your assigned categories:</p>
                    {hasSTCategories && (
                        <div style={{ marginTop: '8px' }}>
                            <strong>Single Tenancy:</strong>
                            <ul>
                                {approverCategoriesSingleTenancy.map(category => (
                                    <li key={category}>{category}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {hasMTCategories && (
                        <div style={{ marginTop: '8px' }}>
                            <strong>Multi Tenancy:</strong>
                            <ul>
                                {approverCategoriesMultiTenancy.map(category => (
                                    <li key={category}>{category === 'PROD' ? 'PROD / POC' : category}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {!hasSTCategories && !hasMTCategories && approverCategories && approverCategories.length > 0 && (
                        <ul>
                            {approverCategories.map(category => (
                                <li key={category}>{category}</li>
                            ))}
                        </ul>
                    )}
                </Message>
            );
        }
        if (isApprover && !hasAnyCategories) {
            return (
                <Message warning>
                    <Message.Header>No Categories Assigned</Message.Header>
                    <p>You are assigned as an approver but no categories have been assigned to you. Please contact an administrator to assign categories to your approver role.</p>
                </Message>
            );
        }
        return (
            <Message info>
                <Message.Header>No Pending Requests</Message.Header>
                <p>There are no requests pending your approval at this time.</p>
            </Message>
        );
    };

    const handleApprove = async (request) => {
        try {
            setProcessingApprovalId(request.id);

            // Check if this is an auto-provision request for the current user
            const autoProvisionForUser = isAutoProvisionRequestForUser(request);

            if (autoProvisionForUser) {
                // For auto-provision requests, directly start creation process
                setShowDetailsModal(false);
                if (isRequestForm3(request)) {
                    // For Multi Tenancy Form, directly start the approval process
                    setSelectedRequest(request);
                    await processForm3Approval(request);
                    await fetchRequests();
                } else {
                    // For regular requests, show subscription selection modal to start creation
                    setSelectedRequest(request);
                    setShowSubscriptionModal(true);
                }
                return;
            }

            if (isApprover || (isSuperAdmin && isMigrationRequest(request))) {
                // Close the details modal when approve is clicked for approvers or DevOps (for migration requests)
                setShowDetailsModal(false);
                // Check if this is a Multi Tenancy Form submission
                if (isRequestForm3(request)) {
                    // For Multi Tenancy Form, directly start the approval process without subscription modal
                    setSelectedRequest(request);
                    await processForm3Approval(request);
                    await fetchRequests();
                } else {
                    // For regular requests, show subscription selection modal
                    setSelectedRequest(request);
                    setShowSubscriptionModal(true);
                }
                return;
            }
        } catch (error) {
            console.error('Error in approval:', error);
            setError(error.response?.data?.message || 'Failed to approve');
            setProcessingApprovalId(null);
        }
    };

    // New function to handle Multi Tenancy Form approval
    const processForm3Approval = async (request) => {
        try {
            setLogViewRequestId(request.id);
            setShowLogsModal(true);
            setCreationLogs([]);
            setIsCancelled(false);
            setProcessingApprovalId(request.id);

            const controller = new AbortController();
            const signal = controller.signal;
            setAbortController(controller);

            try {
                const response = await fetch('https://qa-tenantbridge.parablu.com/api/approve-form3', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache',
                        'Connection': 'keep-alive',
                        'Keep-Alive': 'timeout=600'
                    },
                    body: JSON.stringify({
                        requestId: request.id,
                        approverEmail: userEmail
                    }),
                    credentials: 'include',
                    signal,
                    keepalive: true
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    try {
                        const { value, done } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (!line.trim()) continue;
                            try {
                                const data = JSON.parse(line);
                                if (data.type === 'log') {
                                    // Fallback while Form3 may not yet write to provisioning store
                                    setCreationLogs(prev => [...prev, data.message]);
                                } else if (data.type === 'error') {
                                    setVMCreationError({
                                        errorLocation: data.errorLocation || 'Multi Tenancy Form Processing',
                                        error: data.message,
                                        requestId: data.requestId || request.id,
                                        cloudName: data.cloudName || getCloudName(request)
                                    });
                                    setShowErrorModal(true);
                                }
                            } catch (parseError) {
                                // ignore non-JSON keepalives
                            }
                        }
                    } catch (readError) {
                        if (readError.name === 'AbortError') break;
                        console.warn('Form3 stream read issue:', readError.message);
                        await new Promise(resolve => setTimeout(resolve, 3000));
                        continue;
                    }
                }

                await fetchRequests();
                setProcessingApprovalId(null);
            } catch (error) {
                console.error('Error in Multi Tenancy Form streaming request:', error);

                if (error.name === 'AbortError') {
                    setIsCancelled(true);
                } else if (!(error.name === 'TypeError' && error.message === 'network error')) {
                    setVMCreationError({
                        errorLocation: 'Multi Tenancy Form Processing',
                        error: error.message,
                        requestId: request.id,
                        cloudName: getCloudName(request)
                    });
                    setShowErrorModal(true);
                }

                try {
                    await fetchRequests();
                } catch (refreshError) {
                    console.error('Error refreshing requests:', refreshError);
                }
                setProcessingApprovalId(null);
            }
        } catch (error) {
            console.error('Error in Multi Tenancy Form approval wrapper:', error);
            console.error('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
            setProcessingApprovalId(null);
        }
    };

    const fetchSubscriptions = async () => {
        try {
            setLoadingSubscriptions(true);
            const response = await axios.get('https://qa-tenantbridge.parablu.com/api/subscriptions', {
                withCredentials: true
            });

            if (response.data.subscriptions) {
                setSubscriptions(response.data.subscriptions);
            } else {
                setSubscriptions([{
                    id: process.env.REACT_APP_MACC_SUBSCRIPTION_ID,
                    name: 'MACC May 2023 - April 2028 Subscription'
                }, {
                    id: process.env.REACT_APP_CPG_TEST_1_SUBSCRIPTION_ID,
                    name: 'CPG_TEST_1 Subscription'
                }, {
                    id: process.env.REACT_APP_POC_SUBSCRIPTION_ID,
                    name: 'Parablu POC Demo Subscription'
                }, {
                    id: process.env.REACT_APP_DEV_QA_SUBSCRIPTION_ID,
                    name: 'Parablu Dev QA Subscription'
                }, {
                    id: process.env.REACT_APP_DEV_QA_SUBSCRIPTION_ID,
                    name: 'Parablu DevOps Subscription'
                }]);
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            setSubscriptions([{
                id: process.env.REACT_APP_MACC_SUBSCRIPTION_ID,
                name: 'MACC May 2023 - April 2028 Subscription'
            }, {
                id: process.env.REACT_APP_CPG_TEST_1_SUBSCRIPTION_ID,
                name: 'CPG_TEST_1 Subscription'
            }, {
                id: process.env.REACT_APP_POC_SUBSCRIPTION_ID,
                name: 'Parablu POC Demo Subscription'
            }, {
                id: process.env.REACT_APP_DEV_QA_SUBSCRIPTION_ID,
                name: 'Parablu Dev QA Subscription'
            }, {
                id: process.env.REACT_APP_DEVOPS_SUBSCRIPTION_ID,
                name: 'Parablu DevOps Subscription'
            }]);
        } finally {
            setLoadingSubscriptions(false);
        }
    };


    const handleSubscriptionConfirm = async (subscriptionId, tenantId, clientId = null, provider = 'azure', linodeAccount = null, selectedTenant = null, imageVersion = 'old') => {
        try {
            setShowSubscriptionModal(false);

            if (!selectedRequest) {
                throw new Error('No request selected');
            }

            // Keep the selected imageVersion on the request object so View Details can show the correct Patch Version
            if (imageVersion && selectedRequest) {
                setSelectedRequest(prev =>
                    prev
                        ? {
                            ...prev,
                            formData: {
                                ...prev.formData,
                                imageVersion
                            }
                        }
                        : prev
                );
            }

            // Check which provider was selected and call appropriate approval function
            if (provider === 'linode') {
                console.log('Routing to Linode approval with account:', linodeAccount);
                await processLinodeApproval(selectedRequest, linodeAccount, imageVersion);
            } else {
                console.log('Routing to Azure approval');
                await processApproverApproval(selectedRequest, subscriptionId, tenantId, clientId, selectedTenant, imageVersion);
            }

            await fetchRequests();
        } catch (error) {
            console.error('Error processing approval:', error);
            setError(error.response?.data?.message || 'Failed to approve with selected provider');
            // Clear processing state on error
            setProcessingApprovalId(null);
        }
        // Note: processingApprovalId is cleared by processApproverApproval/processLinodeApproval when stream completes
    };
    // Updated processLinodeApproval function for ApprovalView.jsx
    const processLinodeApproval = async (request, linodeAccount = null, imageVersion = 'old') => {
        try {
            setLogViewRequestId(request.id);
            setShowLogsModal(true);
            setCreationLogs([]);
            setIsCancelled(false);
            setProcessingApprovalId(request.id);

            const controller = new AbortController();
            const signal = controller.signal;
            setAbortController(controller);

            try {
                const response = await fetch('https://qa-tenantbridge.parablu.com/api/approve-linode', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache',
                        'Connection': 'keep-alive',
                        'Keep-Alive': 'timeout=600'
                    },
                    body: JSON.stringify({
                        requestId: request.id,
                        approverEmail: userEmail,
                        linodeAccount: linodeAccount,
                        imageVersion: imageVersion,
                        diskConfiguration: {
                            dataDiskSizeGB: request.formData.dataDiskSizeGB || 1000,
                            createDataDisk: true,
                            setupSwap: true
                        }
                    }),
                    credentials: 'include',
                    signal,
                    keepalive: true
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    try {
                        const { value, done } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (!line.trim()) continue;
                            try {
                                const data = JSON.parse(line);
                                if (data.type === 'log') {
                                    // Fallback while Linode may not yet write to provisioning store
                                    setCreationLogs(prev => [...prev, data.message]);
                                } else if (data.type === 'error') {
                                    setVMCreationError({
                                        errorLocation: data.errorLocation || 'Linode Creation',
                                        error: data.message,
                                        requestId: data.requestId || request.id,
                                        cloudName: data.cloudName || getCloudName(request)
                                    });
                                    setShowErrorModal(true);
                                }
                            } catch (parseError) {
                                // ignore non-JSON keepalives
                            }
                        }
                    } catch (readError) {
                        if (readError.name === 'AbortError') break;
                        console.warn('Linode stream read issue:', readError.message);
                        await new Promise(resolve => setTimeout(resolve, 3000));
                        continue;
                    }
                }

                await fetchRequests();
                setProcessingApprovalId(null);
            } catch (error) {
                console.error('Error in Linode streaming request:', error);

                if (error.name === 'AbortError') {
                    setIsCancelled(true);
                } else if (!(error.name === 'TypeError' && error.message === 'network error')) {
                    setVMCreationError({
                        errorLocation: 'Linode Creation',
                        error: error.message,
                        requestId: request.id,
                        cloudName: getCloudName(request)
                    });
                    setShowErrorModal(true);
                }

                try {
                    await fetchRequests();
                } catch (refreshError) {
                    console.error('Error refreshing requests:', refreshError);
                }
                setProcessingApprovalId(null);
            }
        } catch (error) {
            console.error('Error in Linode approval wrapper:', error);
            console.error('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
            setProcessingApprovalId(null);
        }
    };

    const processApproverApproval = async (request, subscriptionId = null, tenantId = null, clientId = null, selectedTenant = null, imageVersion = 'old') => {
        try {
            setLogViewRequestId(request.id);
            setShowLogsModal(true);
            setCreationLogs([]);
            setIsCancelled(false);
            setProcessingApprovalId(request.id);

            const controller = new AbortController();
            const signal = controller.signal;
            setAbortController(controller);

            try {
                // Kick off provisioning; UI logs come from store + socket/poll (not this stream).
                const response = await fetch('https://qa-tenantbridge.parablu.com/api/approve', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache',
                        'Connection': 'keep-alive',
                        'Keep-Alive': 'timeout=600'
                    },
                    body: JSON.stringify({
                        requestId: request.id,
                        approverEmail: userEmail,
                        subscriptionId: subscriptionId,
                        tenantId: tenantId,
                        clientId: clientId,
                        selectedTenant: selectedTenant,
                        imageVersion: imageVersion
                    }),
                    credentials: 'include',
                    signal,
                    keepalive: true
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    try {
                        const { value, done } = await reader.read();
                        if (done) break;

                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (!line.trim()) continue;
                            try {
                                const data = JSON.parse(line);
                                // Ignore stream log lines for UI — persistedLogs handles display.
                                // Still surface terminal errors in the error modal.
                                if (data.type === 'error') {
                                    setVMCreationError({
                                        errorLocation: data.errorLocation || 'VM Creation/Setup',
                                        error: data.message,
                                        requestId: data.requestId || request.id,
                                        cloudName: data.cloudName || getCloudName(request)
                                    });
                                    setShowErrorModal(true);
                                }
                            } catch (parseError) {
                                // ignore non-JSON keepalives
                            }
                        }
                    } catch (readError) {
                        if (readError.name === 'AbortError') break;
                        // Stream can stall; provisioning continues server-side — keep modal on store logs
                        console.warn('Approve stream read issue (continuing via persisted logs):', readError.message);
                        await new Promise(resolve => setTimeout(resolve, 3000));
                        continue;
                    }
                }

                await fetchRequests();
                setProcessingApprovalId(null);
            } catch (error) {
                console.error('Error in streaming request:', error);

                if (error.name === 'AbortError') {
                    setIsCancelled(true);
                } else if (!(error.name === 'TypeError' && error.message === 'network error')) {
                    setVMCreationError({
                        errorLocation: 'VM Creation/Setup',
                        error: error.message,
                        requestId: request.id,
                        cloudName: getCloudName(request)
                    });
                    setShowErrorModal(true);
                }

                try {
                    await fetchRequests();
                } catch (refreshError) {
                    console.error('Error refreshing requests:', refreshError);
                }
                setProcessingApprovalId(null);
            }
        } catch (error) {
            console.error('Error in approver approval wrapper:', error);
            console.error('Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
            setProcessingApprovalId(null);
        }
    };

    const handleReject = async (reason) => {
        try {
            if (!selectedRequest) return;

            setProcessingRejectionId(selectedRequest.id);

            const endpoint = 'reject';

            const response = await axios.post(`https://qa-tenantbridge.parablu.com/api/${endpoint}`, {
                requestId: selectedRequest.id,
                rejectionReason: reason,
                rejectedBy: userEmail
            }, { withCredentials: true });

            setShowRejectionModal(false);
            setRejectedRequest(selectedRequest);
            setShowRejectionSuccessModal(true);
            setSelectedRequest(null);
            await fetchRequests();

        } catch (err) {
            console.error('Error rejecting request:', err);
            setError('Failed to reject request: ' + (err.response?.data?.message || err.message));
        } finally {
            setProcessingRejectionId(null);
        }
    };

    const handleMarkCompleted = async (request) => {
        try {
            setProcessingApprovalId(request.id);

            const response = await axios.post('https://qa-tenantbridge.parablu.com/api/mark-completed', {
                requestId: request.id
            }, { withCredentials: true });

            if (response.data.success) {
                setInfoBanner(`Request ${request.id} has been marked as completed successfully.`);
                setTimeout(() => setInfoBanner(''), 5000);
                await fetchRequests();
            } else {
                setError('Failed to mark request as completed: ' + (response.data.message || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error marking request as completed:', err);
            setError('Failed to mark request as completed: ' + (err.response?.data?.message || err.message));
        } finally {
            setProcessingApprovalId(null);
        }
    };

    const isProcessing =
        (processingApprovalId !== null && processingApprovalId !== undefined) ||
        (processingRejectionId !== null && processingRejectionId !== undefined);

    // Sorted + paginated requests for table
    const sortedRequests = useMemo(() => {
        const items = requests.slice();
        const directionFactor = sortDirection === 'descending' ? -1 : 1;

        const getFieldValue = (req, column) => {
            switch (column) {
                case 'timestamp':
                    return new Date(req.timestamp).getTime() || 0;
                case 'requestId':
                    return String(req.id || '').toLowerCase();
                case 'submittedBy':
                    return getSubmittedBy(req).toLowerCase();
                case 'category':
                    return String(req.category || '').toLowerCase();
                case 'cloudName':
                    return getCloudName(req).toLowerCase();
                default:
                    return '';
            }
        };

        items.sort((a, b) => {
            const aVal = getFieldValue(a, sortColumn);
            const bVal = getFieldValue(b, sortColumn);

            if (aVal < bVal) return -1 * directionFactor;
            if (aVal > bVal) return 1 * directionFactor;
            return 0;
        });

        return items;
    }, [requests, sortColumn, sortDirection]);

    const totalPages = Math.max(1, Math.ceil(sortedRequests.length / Math.max(1, rowsPerPage)));
    const pageStart = (activePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const visibleRequests = sortedRequests.slice(pageStart, pageEnd);

    useEffect(() => {
        setProcessingApprovalId(null);
        setProcessingRejectionId(null);

        fetchRequests();
    }, [userEmail, userRoles, approverCategories]);

    useEffect(() => {
        setProcessingApprovalId(null);
        setProcessingRejectionId(null);
        setSelectedRequest(null);

        return () => {
            setProcessingApprovalId(null);
            setProcessingRejectionId(null);
            setSelectedRequest(null);
        };
    }, []);

    if (loading) return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <Loader active inline='centered'>Loading requests...</Loader>
        </div>
    );

    if (error) return <Message negative>{error}</Message>;

    return (
        <Container fluid className="page-fade-in" style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 200px)',
            minHeight: '600px',
            position: 'relative'
        }}>
            {/* Fixed Header */}
            <div style={{
                flexShrink: 0,
                backgroundColor: 'white',
                zIndex: 100,
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '1.35rem',
                        fontWeight: 700,
                        color: '#062D4D',
                        marginBottom: infoBanner ? '0.75rem' : '0'
                    }}
                >
                    <Icon name="clock outline" color="blue" />
                    <span>Pending Approvals</span>
                </div>
                {infoBanner && (
                    <Message positive style={{ marginBottom: '1rem' }}>
                        {infoBanner}
                    </Message>
                )}
            </div>

            {/* Scrollable Table Container */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'auto',
                minHeight: 0,
                position: 'relative'
            }}>
                {requests.length === 0 ? (
                    <EmptyState 
                        userRoles={userRoles} 
                        approverCategories={approverCategories}
                        approverCategoriesSingleTenancy={approverCategoriesSingleTenancy}
                        approverCategoriesMultiTenancy={approverCategoriesMultiTenancy}
                    />
                ) : (
                    <>
                        <Table celled sortable compact={tableDensity === 'compact'} size={tableDensity === 'compact' ? 'small' : undefined}>
                            <Table.Header style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <Table.Row>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'timestamp' ? sortDirection : null}
                                        onClick={() => handleSort('timestamp')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Timestamp
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'requestId' ? sortDirection : null}
                                        onClick={() => handleSort('requestId')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Request ID
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'submittedBy' ? sortDirection : null}
                                        onClick={() => handleSort('submittedBy')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Submitted By
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'category' ? sortDirection : null}
                                        onClick={() => handleSort('category')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Category
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === 'cloudName' ? sortDirection : null}
                                        onClick={() => handleSort('cloudName')}
                                        style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                    >
                                        Cloud Name
                                    </Table.HeaderCell>
                                    <Table.HeaderCell style={{ backgroundColor: 'white' }}>Actions</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {visibleRequests.map(request => (
                                        <Table.Row key={request.id}>
                                            <Table.Cell>{formatTimestamp(request.timestamp)}</Table.Cell>
                                            <Table.Cell>{request.id}</Table.Cell>
                                            <Table.Cell>{getSubmittedBy(request)}</Table.Cell>
                                            <Table.Cell>
                                                {request.category === 'PROD' && request.formData?.requestForm3
                                                    ? 'PROD / POC'
                                                    : (request.category || 'Unknown')}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {getCloudName(request)}
                                                {getBatchBadges(request).map((badge, idx) => (
                                                    <span
                                                        key={idx}
                                                        style={{
                                                            marginLeft: idx === 0 ? '8px' : '4px',
                                                            padding: '2px 6px',
                                                            backgroundColor: '#e1f5fe',
                                                            color: '#35B7F4',
                                                            borderRadius: '3px',
                                                            fontSize: '0.8em',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {badge}
                                                    </span>
                                                ))}
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                                                    <Button
                                                        size="small"
                                                        content="View Details"
                                                        disabled={isProcessing}
                                                        onClick={() => {
                                                            setSelectedRequest(request);
                                                            setShowDetailsModal(true);
                                                        }}
                                                    />
                                                    {request.status === 'MIGRATION IS IN PROGRESS' && (
                                                        <Button
                                                            size="small"
                                                            color="green"
                                                            content="Tenant is Ready"
                                                            disabled={isProcessing || processingApprovalId === request.id}
                                                            loading={processingApprovalId === request.id}
                                                            onClick={() => handleMarkCompleted(request)}
                                                        />
                                                    )}
                                                    <Popup
                                                        on='click'
                                                        position='bottom right'
                                                        pinned
                                                        mouseLeaveDelay={0}
                                                        offset={[0, 8]}
                                                        style={{ zIndex: 2000 }}
                                                        trigger={<Button size="small" icon><Icon name="ellipsis vertical" /></Button>}
                                                        content={
                                                            <Menu vertical secondary size='small' style={{ minWidth: 180, fontSize: '0.9em' }}>
                                                                <Menu.Item style={{ padding: '6px 10px' }} onClick={() => copyToClipboard(request.id, 'Request ID copied')}>
                                                                    <Icon name='copy' size='small' /> Copy ID
                                                                </Menu.Item>
                                                            </Menu>
                                                        }
                                                    />
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                            </Table.Body>
                        </Table>
                    </>
                )}
            </div>

            {/* Fixed Pagination */}
            {sortedRequests.length > 0 && (
                <div style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'white',
                    padding: '8px 10px',
                    borderTop: '1px solid rgba(0,0,0,0.08)',
                    zIndex: 100,
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.05)'
                }}>
                    <div style={{ color: '#666', fontSize: '0.9em' }}>
                        {(() => {
                            const total = sortedRequests.length;
                            const start = total === 0 ? 0 : (activePage - 1) * rowsPerPage + 1;
                            const end = Math.min(activePage * rowsPerPage, total);
                            return `Showing ${start}-${end} of ${total}`;
                        })()}
                    </div>
                    <Pagination
                        activePage={activePage}
                        totalPages={totalPages}
                        onPageChange={(e, data) => setActivePage(Number(data.activePage))}
                        size="small"
                        siblingRange={1}
                        boundaryRange={0}
                        ellipsisItem={null}
                        firstItem={null}
                        lastItem={null}
                        prevItem={{ content: 'Prev', icon: 'angle left' }}
                        nextItem={{ content: 'Next', icon: 'angle right' }}
                    />
                </div>
            )}

            <RejectionSuccessModal
                open={showRejectionSuccessModal}
                onClose={() => {
                    setShowRejectionSuccessModal(false);
                    setRejectedRequest(null);
                }}
                isApprover={isApprover}
                requestData={rejectedRequest}
            />

            <ApprovalSuccessModal
                open={showApprovalSuccessModal}
                onClose={() => setShowApprovalSuccessModal(false)}
                requestData={selectedRequest}
                userEmail={userEmail}
                requestCategory={selectedRequest?.category}
            />

            <RequestDetailsModal
                open={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedRequest(null);
                }}
                request={selectedRequest}
                showApprovalActions={isApprover}
                isApprover={isApprover}
                onApprove={handleApprove}
                onReject={() => {
                    if (selectedRequest) {
                        // Close the details modal before showing rejection modal
                        setShowDetailsModal(false);
                        setShowRejectionModal(true);
                    }
                }}
                onMarkCompleted={handleMarkCompleted}
                processingApprovalId={processingApprovalId}
                processingRejectionId={processingRejectionId}
                isProcessing={isProcessing}
            />

            <RejectionModal
                open={showRejectionModal}
                onClose={() => {
                    setShowRejectionModal(false);
                    // Keep selectedRequest set in case user wants to retry rejection
                    // It will be cleared after successful rejection in handleReject
                }}
                onSubmit={handleReject}
                isSubmitting={processingRejectionId !== null}
            />

            <CreationLogsModal
                open={showLogsModal}
                onClose={() => {
                    // Closing does NOT cancel — reopen from Approver History / All Approval History
                    setShowLogsModal(false);
                }}
                onCancel={handleCancelOperation}
                isCancelled={isCancelled}
                logs={persistedLogs.length > 0 ? persistedLogs : creationLogs}
                sessionStartedAt={provisioningStartedAt}
            />

            <VMCreationErrorModal
                open={showErrorModal}
                onClose={() => {
                    setShowErrorModal(false);
                    setVMCreationError(null);
                    fetchRequests();
                }}
                {...vmCreationError}
            />

            <SubscriptionSelectionModal
                open={showSubscriptionModal}
                onClose={() => {
                    setShowSubscriptionModal(false);
                    setProcessingApprovalId(null);
                }}
                onConfirm={handleSubscriptionConfirm}
                requestData={selectedRequest}
                subscriptions={subscriptions}
                linodeEnabled={linodeEnabled}
            />

        </Container>
    );
};

export default ApprovalView;